import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useApi } from './api-context';

export interface MeCompany {
  id: string;
  name: string;
}

export interface MeData {
  auth_subject: string;
  /** null si el token no trae email y el usuario aún no tiene perfil en el QMS. */
  email: string | null;
  name: string | null;
  user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  roles: string[];
  permissions: string[];
  active_company_id: string | null;
  company_ids: string[];
  companies: MeCompany[];
  cross_company: boolean;
  /** Documentos que puede abrir: todos (rol con lectura), solo los concedidos por un
   *  acceso temporal de proveedor, o ninguno. Lo decide el backend. */
  document_access?: 'all' | 'granted' | 'none';
}

interface MeResponse {
  success: true;
  data: MeData;
}

export type CompanySlug = 'grupodim' | 'deinsa' | 'panama' | 'morro';

/** Slug de empresa para teñir el acento (data-empresa). null → acento por defecto. */
export function companySlug(name: string): CompanySlug | null {
  const n = name.toLowerCase();
  if (n.includes('morro')) {
    return 'morro';
  }
  if (n.includes('panam')) {
    return 'panama';
  }
  if (n.includes('deinsa')) {
    return 'deinsa';
  }
  if (n.includes('dim') || n.includes('grupo')) {
    return 'grupodim';
  }
  return null;
}

/** Iniciales para el avatar del usuario en el header (a partir de un nombre/email). */
export function meInitials(
  data: Pick<MeData, 'first_name' | 'last_name' | 'name' | 'email'>,
): string {
  // Cualquier dato puede faltar (usuario recién aprovisionado o sin claims): se toma
  // el primero con contenido real y, si no hay ninguno, un marcador neutro.
  const source = [
    [data.first_name, data.last_name].filter(Boolean).join(' '),
    data.name,
    data.email,
  ]
    .map((s) => s?.trim() ?? '')
    .find((s) => s.length > 0);
  if (source === undefined) {
    return '?';
  }
  const tokens = source.split(/\s+/);
  const first = tokens[0] ?? '';
  const last = tokens[tokens.length - 1] ?? '';
  const second = tokens.length > 1 ? last.charAt(0) : first.charAt(1);
  return (first.charAt(0) + second).toUpperCase();
}

/** Perfil del usuario (/me). Cacheado; fuente de permisos y empresas. */
export function useMe(): UseQueryResult<MeResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['me'],
    // Sin X-Active-Company: /me resuelve las empresas del usuario y no debe fallar si
    // la empresa elegida dejó de ser suya (el provider valida la elección contra ellas).
    queryFn: () => api.get<MeResponse>('/api/v1/me', { omitActiveCompany: true }),
    staleTime: 5 * 60 * 1000,
    // Sin conexión la consulta se intenta igual y termina en error: en modo 'online'
    // quedaría en pausa y las pantallas mostrarían "cargando" sin fin.
    networkMode: 'always',
  });
}
